import { config } from '../config';

export interface FossilFillsRequest {
  prompt: string;
  domain?: string;
  user_context?: Record<string, any>;
}

export interface BlankDefinition {
  id: string;
  type: string;
  label: string;
  placeholder: string;
  suggestions: string[];
  required: boolean;
  help_text: string;
  position: number;
  original_text?: string;
}

export interface FossilTemplate {
  template: string;
  blanks: BlankDefinition[];
  domain: string;
  educational_notes: string[];
  original_prompt: string;
  basic_improvement: string;
}

export interface FossilFillsResponse {
  fossil_template: FossilTemplate;
  usage: {
    daily_count: number;
    weekly_count: number;
    daily_limit: number;
    weekly_limit: number;
  };
  success: boolean;
}

class FossilFillsApi {
  private apiEndpoint: string;

  constructor() {
    this.apiEndpoint = config.api.baseUrl;
    console.log('FossilFillsApi initialized with endpoint:', this.apiEndpoint);
  }

  async getAvailableDomains(): Promise<{ domains: string[], count: number }> {
    try {
      const response = await fetch(`${this.apiEndpoint}/api/fossil_fills/domains`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch domains:', error);
      // Return default domains as fallback
      return {
        domains: ['general', 'screenwriting', 'creative_writing', 'business', 'technical', 'academic', 'marketing', 'programming'],
        count: 8
      };
    }
  }

  async createTemplate(prompt: string, domain?: string): Promise<FossilFillsResponse> {
    console.log('Creating Fossil template for prompt:', prompt);
    console.log('API endpoint:', this.apiEndpoint);
    console.log('Full URL:', `${this.apiEndpoint}/api/fossil_fills`);
    
    try {
      const response = await fetch(`${this.apiEndpoint}/api/fossil_fills`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        },
        body: JSON.stringify({
          prompt,
          domain,
          user_context: this.getUserContext()
        } as FossilFillsRequest)
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      console.log('API Response:', JSON.stringify(data, null, 2));
      return data as FossilFillsResponse;
    } catch (error) {
      console.error('Failed to create Fossil template:', error);
      console.error('API Endpoint:', this.apiEndpoint);
      console.error('Full URL:', `${this.apiEndpoint}/api/fossil_fills`);
      // Re-throw the error instead of returning mock data
      throw error;
    }
  }

  async fillTemplate(
    template: string, 
    filledBlanks: Record<string, string>
  ): Promise<string> {
    // Process template with filled blanks
    let result = template;
    
    Object.entries(filledBlanks).forEach(([blankId, value]) => {
      // Updated regex to match any characters (not just word chars) in the default value
      const regex = new RegExp(`\\{\\{${blankId}:[^}]+\\}\\}`, 'g');
      result = result.replace(regex, value);
    });
    
    return result;
  }

  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('authToken');
    if (token) {
      return { 'Authorization': `Bearer ${token}` };
    }
    return {};
  }

  private getUserContext(): Record<string, any> {
    const authToken = localStorage.getItem('authToken');
    const userId = localStorage.getItem('userId');
    
    return {
      authenticated: !!authToken,
      userId: userId || 'anonymous',
      timestamp: new Date().toISOString()
    };
  }

  private getMockResponse(prompt: string): FossilFillsResponse {
    // Mock response for development
    return {
      fossil_template: {
        template: "You are a {{role_0:role}} who specializes in {{domain_0:domain}}. Create a {{output_0:format}} that is {{style_0:style}} for {{audience_0:audience}}.",
        blanks: [
          {
            id: "role_0",
            type: "role",
            label: "Your expertise or perspective",
            placeholder: "expert role",
            suggestions: ["senior developer", "technical writer", "consultant", "educator", "specialist", "Custom..."],
            required: true,
            help_text: "Define your expertise level and perspective for the AI to adopt",
            position: 0,
            original_text: "helpful assistant"
          },
          {
            id: "domain_0",
            type: "domain",
            label: "Your specialty area",
            placeholder: "domain",
            suggestions: ["web development", "data science", "machine learning", "software architecture", "Custom..."],
            required: true,
            help_text: "Specify your area of expertise",
            position: 1
          },
          {
            id: "output_0",
            type: "format",
            label: "Output format",
            placeholder: "format type",
            suggestions: ["detailed guide", "step-by-step tutorial", "comprehensive analysis", "technical documentation", "Custom..."],
            required: true,
            help_text: "Specify the structure and format of the output",
            position: 2
          },
          {
            id: "style_0",
            type: "style",
            label: "Communication style",
            placeholder: "style",
            suggestions: ["clear and concise", "detailed", "professional", "engaging", "technical", "Custom..."],
            required: true,
            help_text: "Set the tone and communication style for the response",
            position: 3
          },
          {
            id: "audience_0",
            type: "audience",
            label: "Target audience",
            placeholder: "audience",
            suggestions: ["beginners", "professionals", "developers", "students", "general audience", "Custom..."],
            required: true,
            help_text: "Identify who will be consuming this content",
            position: 4
          }
        ],
        domain: "general",
        educational_notes: [
          "🎯 Specifying your role helps the AI adopt the right expertise level",
          "💡 Clear output format requests lead to better structured responses",
          "📊 Defining your audience ensures appropriate complexity and terminology",
          "✨ Communication style sets the tone for the entire interaction"
        ],
        original_prompt: prompt,
        basic_improvement: `You are a helpful assistant. ${prompt} Please provide a comprehensive and detailed response.`
      },
      usage: {
        daily_count: 2,
        weekly_count: 5,
        daily_limit: 10,
        weekly_limit: 30
      },
      success: true
    };
  }
}

export const fossilFillsApi = new FossilFillsApi();